/**
 * Application Mock Data
 *
 * Sample halal certification applications for the HALMAS MVP.
 * Demonstrates various application types, statuses, and workflows.
 */

import type {
  Application,
  ApplicationDocument,
  ApplicationStatus,
  ApplicationType,
  DocumentType,
  ReviewNote,
  StatusHistory,
} from '../types/application';

/**
 * Mock application documents
 */
const mockDocuments: ApplicationDocument[] = [
  {
    id: 'doc-001',
    type: 'BUSINESS_LICENSE' as DocumentType,
    name: 'SSM Business Registration',
    fileName: 'ssm-registration-2024.pdf',
    fileSize: 524288, // 512 KB
    mimeType: 'application/pdf',
    url: '/mock/documents/ssm-registration-2024.pdf',
    uploadedAt: new Date('2025-01-15T10:30:00'),
    uploadedBy: 'user-001', // Ahmad bin Ibrahim (Company Admin)
    verified: true,
    verifiedBy: 'user-007', // Sarah Lee (CB Admin)
    verifiedAt: new Date('2025-01-16T14:00:00'),
  },
  {
    id: 'doc-002',
    type: 'HALAL_POLICY' as DocumentType,
    name: 'Halal Management Policy',
    fileName: 'halal-policy-v2.pdf',
    fileSize: 1048576, // 1 MB
    mimeType: 'application/pdf',
    url: '/mock/documents/halal-policy-v2.pdf',
    uploadedAt: new Date('2025-01-15T11:00:00'),
    uploadedBy: 'user-001',
    verified: true,
    verifiedBy: 'user-003', // Dr. Zainab Ali (Consultant)
    verifiedAt: new Date('2025-01-17T09:30:00'),
  },
  {
    id: 'doc-003',
    type: 'FACILITY_LAYOUT' as DocumentType,
    name: 'Factory Floor Plan',
    fileName: 'facility-layout-2025.pdf',
    fileSize: 2097152, // 2 MB
    mimeType: 'application/pdf',
    url: '/mock/documents/facility-layout-2025.pdf',
    uploadedAt: new Date('2025-01-15T11:30:00'),
    uploadedBy: 'user-001',
    verified: false,
  },
  {
    id: 'doc-004',
    type: 'INGREDIENT_LIST' as DocumentType,
    name: 'Ingredient Specifications',
    fileName: 'ingredient-list.xlsx',
    fileSize: 327680, // 320 KB
    mimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    url: '/mock/documents/ingredient-list.xlsx',
    uploadedAt: new Date('2025-01-15T12:00:00'),
    uploadedBy: 'user-002', // Siti Aminah (Company Staff)
    verified: true,
    verifiedBy: 'user-003',
    verifiedAt: new Date('2025-01-17T10:00:00'),
  },
  {
    id: 'doc-005',
    type: 'SUPPLIER_CERTIFICATES' as DocumentType,
    name: 'Supplier Halal Certificates',
    fileName: 'supplier-certs.zip',
    fileSize: 5242880, // 5 MB
    mimeType: 'application/zip',
    url: '/mock/documents/supplier-certs.zip',
    uploadedAt: new Date('2025-01-16T09:00:00'),
    uploadedBy: 'user-002',
    verified: true,
    verifiedBy: 'user-003',
    verifiedAt: new Date('2025-01-17T11:00:00'),
  },
];

/**
 * Mock review notes
 */
const mockReviewNotes: ReviewNote[] = [
  {
    id: 'note-001',
    applicationId: 'app-001',
    author: 'user-003',
    authorName: 'Dr. Zainab Ali',
    content: 'Initial review completed. All required documents submitted. Pre-audit recommended before proceeding to official audit.',
    createdAt: new Date('2025-01-17T14:30:00'),
    isInternal: false,
  },
  {
    id: 'note-002',
    applicationId: 'app-001',
    author: 'user-007',
    authorName: 'Sarah Lee',
    content: 'Internal note: Fast-track candidate. Company has good compliance history.',
    createdAt: new Date('2025-01-17T15:00:00'),
    isInternal: true,
  },
  {
    id: 'note-003',
    applicationId: 'app-001',
    author: 'user-005',
    authorName: 'Muhammad Yusof',
    content: 'Pre-audit scheduled for January 25, 2025. Facility inspection and documentation review.',
    createdAt: new Date('2025-01-20T10:00:00'),
    isInternal: false,
  },
];

/**
 * Mock status history
 */
const mockStatusHistory: StatusHistory[] = [
  {
    status: 'DRAFT' as ApplicationStatus,
    changedAt: new Date('2025-01-10T08:00:00'),
    changedBy: 'user-001',
    changedByName: 'Ahmad bin Ibrahim',
    comment: 'Application created',
  },
  {
    status: 'SUBMITTED' as ApplicationStatus,
    changedAt: new Date('2025-01-16T16:00:00'),
    changedBy: 'user-001',
    changedByName: 'Ahmad bin Ibrahim',
    comment: 'All required documents uploaded and submitted for review',
  },
  {
    status: 'UNDER_REVIEW' as ApplicationStatus,
    changedAt: new Date('2025-01-17T09:00:00'),
    changedBy: 'user-007',
    changedByName: 'Sarah Lee',
    comment: 'Application accepted for review',
  },
  {
    status: 'CONSULTANT_ASSIGNED' as ApplicationStatus,
    changedAt: new Date('2025-01-17T09:15:00'),
    changedBy: 'user-007',
    changedByName: 'Sarah Lee',
    comment: 'Assigned to Dr. Zainab Ali for consultation',
  },
  {
    status: 'PRE_AUDIT_SCHEDULED' as ApplicationStatus,
    changedAt: new Date('2025-01-20T10:00:00'),
    changedBy: 'user-003',
    changedByName: 'Dr. Zainab Ali',
    comment: 'Pre-audit scheduled for January 25, 2025',
  },
];

/**
 * Mock applications
 */
export const mockApplications: Application[] = [
  // Application 1: Pre-audit scheduled (active workflow)
  {
    id: 'app-001',
    applicationNumber: 'APP-2025-0001',
    type: 'NEW_CERTIFICATION' as ApplicationType,
    status: 'PRE_AUDIT_SCHEDULED' as ApplicationStatus,

    companyId: 'org-001',
    companyName: 'Nasi Lemak Food Industries Sdn Bhd',
    applicantId: 'user-001',
    applicantName: 'Ahmad bin Ibrahim',

    assignedConsultantId: 'user-003',
    assignedConsultantName: 'Dr. Zainab Ali',
    consultantAssignedAt: new Date('2025-01-17T09:15:00'),

    documents: mockDocuments,
    requiredDocuments: [
      'BUSINESS_LICENSE',
      'HALAL_POLICY',
      'FACILITY_LAYOUT',
      'PROCESS_FLOW',
      'INGREDIENT_LIST',
      'SUPPLIER_CERTIFICATES',
      'TRAINING_RECORDS',
      'SOP_MANUAL',
    ] as DocumentType[],

    completenessScore: 75, // 6 out of 8 documents uploaded

    reviewNotes: mockReviewNotes,
    statusHistory: mockStatusHistory,

    submittedAt: new Date('2025-01-16T16:00:00'),
    targetCompletionDate: new Date('2025-04-16T23:59:59'), // 90 days from submission

    createdAt: new Date('2025-01-10T08:00:00'),
    updatedAt: new Date('2025-01-20T10:00:00'),
    createdBy: 'user-001',
    updatedBy: 'user-003',
  },

  // Application 2: Draft (incomplete)
  {
    id: 'app-002',
    applicationNumber: 'APP-2025-0002',
    type: 'NEW_CERTIFICATION' as ApplicationType,
    status: 'DRAFT' as ApplicationStatus,

    companyId: 'org-001',
    companyName: 'Nasi Lemak Food Industries Sdn Bhd',
    applicantId: 'user-002',
    applicantName: 'Siti Aminah',

    documents: [mockDocuments[0], mockDocuments[1]], // Only 2 documents
    requiredDocuments: [
      'BUSINESS_LICENSE',
      'HALAL_POLICY',
      'FACILITY_LAYOUT',
      'INGREDIENT_LIST',
      'SUPPLIER_CERTIFICATES',
    ] as DocumentType[],

    completenessScore: 40, // 2 out of 5 documents

    reviewNotes: [],
    statusHistory: [
      {
        status: 'DRAFT' as ApplicationStatus,
        changedAt: new Date('2025-01-22T14:00:00'),
        changedBy: 'user-002',
        changedByName: 'Siti Aminah',
        comment: 'Application created for new product line',
      },
    ],

    createdAt: new Date('2025-01-22T14:00:00'),
    updatedAt: new Date('2025-01-23T09:30:00'),
    createdBy: 'user-002',
  },

  // Application 3: Under review
  {
    id: 'app-003',
    applicationNumber: 'APP-2025-0003',
    type: 'RENEWAL' as ApplicationType,
    status: 'UNDER_REVIEW' as ApplicationStatus,

    companyId: 'org-001',
    companyName: 'Nasi Lemak Food Industries Sdn Bhd',
    applicantId: 'user-001',
    applicantName: 'Ahmad bin Ibrahim',

    previousCertificateId: 'cert-001', // Renewing existing certificate

    documents: [mockDocuments[0], mockDocuments[1], mockDocuments[3]],
    requiredDocuments: [
      'BUSINESS_LICENSE',
      'HALAL_POLICY',
      'INGREDIENT_LIST',
      'SUPPLIER_CERTIFICATES',
    ] as DocumentType[],

    completenessScore: 75, // 3 out of 4 documents

    reviewNotes: [
      {
        id: 'note-004',
        applicationId: 'app-003',
        author: 'user-007',
        authorName: 'Sarah Lee',
        content: 'Renewal application received. Previous certificate expires in 30 days.',
        createdAt: new Date('2025-01-18T11:00:00'),
        isInternal: false,
      },
    ],
    statusHistory: [
      {
        status: 'DRAFT' as ApplicationStatus,
        changedAt: new Date('2025-01-15T10:00:00'),
        changedBy: 'user-001',
        changedByName: 'Ahmad bin Ibrahim',
      },
      {
        status: 'SUBMITTED' as ApplicationStatus,
        changedAt: new Date('2025-01-18T09:00:00'),
        changedBy: 'user-001',
        changedByName: 'Ahmad bin Ibrahim',
        comment: 'Certificate renewal submission',
      },
      {
        status: 'UNDER_REVIEW' as ApplicationStatus,
        changedAt: new Date('2025-01-18T11:00:00'),
        changedBy: 'user-007',
        changedByName: 'Sarah Lee',
      },
    ],

    submittedAt: new Date('2025-01-18T09:00:00'),
    targetCompletionDate: new Date('2025-02-28T23:59:59'), // Expedited renewal

    createdAt: new Date('2025-01-15T10:00:00'),
    updatedAt: new Date('2025-01-18T11:00:00'),
    createdBy: 'user-001',
    updatedBy: 'user-007',
  },

  // Application 4: Audit in progress
  {
    id: 'app-004',
    applicationNumber: 'APP-2024-0156',
    type: 'NEW_CERTIFICATION' as ApplicationType,
    status: 'AUDIT_IN_PROGRESS' as ApplicationStatus,

    companyId: 'org-001',
    companyName: 'Nasi Lemak Food Industries Sdn Bhd',
    applicantId: 'user-001',
    applicantName: 'Ahmad bin Ibrahim',

    assignedConsultantId: 'user-004',
    assignedConsultantName: 'Farid Rahman',
    consultantAssignedAt: new Date('2024-12-01T10:00:00'),

    assignedAuditorId: 'user-005',
    assignedAuditorName: 'Muhammad Yusof',
    auditorAssignedAt: new Date('2024-12-15T14:00:00'),

    preAuditId: 'pre-audit-001',
    officialAuditId: 'audit-001',

    documents: mockDocuments,
    requiredDocuments: [
      'BUSINESS_LICENSE',
      'HALAL_POLICY',
      'FACILITY_LAYOUT',
      'PROCESS_FLOW',
      'INGREDIENT_LIST',
      'SUPPLIER_CERTIFICATES',
      'TRAINING_RECORDS',
      'SOP_MANUAL',
    ] as DocumentType[],

    completenessScore: 100,
    readinessScore: 85, // From pre-audit

    reviewNotes: [
      {
        id: 'note-005',
        applicationId: 'app-004',
        author: 'user-004',
        authorName: 'Farid Rahman',
        content: 'Pre-audit completed successfully. Minor improvements needed in training documentation.',
        createdAt: new Date('2024-12-10T16:00:00'),
        isInternal: false,
      },
      {
        id: 'note-006',
        applicationId: 'app-004',
        author: 'user-005',
        authorName: 'Muhammad Yusof',
        content: 'Official audit day 1 completed. Facility inspection satisfactory.',
        createdAt: new Date('2025-01-15T17:00:00'),
        isInternal: false,
      },
    ],
    statusHistory: [
      {
        status: 'DRAFT' as ApplicationStatus,
        changedAt: new Date('2024-11-20T08:00:00'),
        changedBy: 'user-001',
        changedByName: 'Ahmad bin Ibrahim',
      },
      {
        status: 'SUBMITTED' as ApplicationStatus,
        changedAt: new Date('2024-11-28T16:00:00'),
        changedBy: 'user-001',
        changedByName: 'Ahmad bin Ibrahim',
      },
      {
        status: 'CONSULTANT_ASSIGNED' as ApplicationStatus,
        changedAt: new Date('2024-12-01T10:00:00'),
        changedBy: 'user-007',
        changedByName: 'Sarah Lee',
      },
      {
        status: 'PRE_AUDIT_COMPLETED' as ApplicationStatus,
        changedAt: new Date('2024-12-10T16:00:00'),
        changedBy: 'user-004',
        changedByName: 'Farid Rahman',
      },
      {
        status: 'AUDIT_SCHEDULED' as ApplicationStatus,
        changedAt: new Date('2024-12-15T14:00:00'),
        changedBy: 'user-007',
        changedByName: 'Sarah Lee',
        comment: 'Official audit scheduled for January 15-16, 2025',
      },
      {
        status: 'AUDIT_IN_PROGRESS' as ApplicationStatus,
        changedAt: new Date('2025-01-15T08:00:00'),
        changedBy: 'user-005',
        changedByName: 'Muhammad Yusof',
      },
    ],

    submittedAt: new Date('2024-11-28T16:00:00'),
    targetCompletionDate: new Date('2025-02-28T23:59:59'),

    createdAt: new Date('2024-11-20T08:00:00'),
    updatedAt: new Date('2025-01-15T17:00:00'),
    createdBy: 'user-001',
    updatedBy: 'user-005',
  },

  // Application 5: Approved
  {
    id: 'app-005',
    applicationNumber: 'APP-2024-0089',
    type: 'NEW_CERTIFICATION' as ApplicationType,
    status: 'APPROVED' as ApplicationStatus,

    companyId: 'org-001',
    companyName: 'Nasi Lemak Food Industries Sdn Bhd',
    applicantId: 'user-001',
    applicantName: 'Ahmad bin Ibrahim',

    assignedConsultantId: 'user-003',
    assignedConsultantName: 'Dr. Zainab Ali',
    consultantAssignedAt: new Date('2024-09-15T10:00:00'),

    assignedAuditorId: 'user-006',
    assignedAuditorName: 'Hajjah Fatimah',
    auditorAssignedAt: new Date('2024-10-01T10:00:00'),

    preAuditId: 'pre-audit-002',
    officialAuditId: 'audit-002',

    documents: mockDocuments,
    requiredDocuments: [
      'BUSINESS_LICENSE',
      'HALAL_POLICY',
      'FACILITY_LAYOUT',
      'PROCESS_FLOW',
      'INGREDIENT_LIST',
      'SUPPLIER_CERTIFICATES',
      'TRAINING_RECORDS',
      'SOP_MANUAL',
    ] as DocumentType[],

    completenessScore: 100,
    readinessScore: 92,

    reviewNotes: [
      {
        id: 'note-007',
        applicationId: 'app-005',
        author: 'user-006',
        authorName: 'Hajjah Fatimah',
        content: 'Audit completed successfully. No major non-conformities found. Recommend approval.',
        createdAt: new Date('2024-11-20T15:00:00'),
        isInternal: false,
      },
      {
        id: 'note-008',
        applicationId: 'app-005',
        author: 'user-007',
        authorName: 'Sarah Lee',
        content: 'Application approved. Certificate will be issued within 5 business days.',
        createdAt: new Date('2024-12-01T10:00:00'),
        isInternal: false,
      },
    ],
    statusHistory: [
      {
        status: 'DRAFT' as ApplicationStatus,
        changedAt: new Date('2024-09-01T08:00:00'),
        changedBy: 'user-001',
        changedByName: 'Ahmad bin Ibrahim',
      },
      {
        status: 'SUBMITTED' as ApplicationStatus,
        changedAt: new Date('2024-09-10T16:00:00'),
        changedBy: 'user-001',
        changedByName: 'Ahmad bin Ibrahim',
      },
      {
        status: 'AUDIT_COMPLETED' as ApplicationStatus,
        changedAt: new Date('2024-11-20T15:00:00'),
        changedBy: 'user-006',
        changedByName: 'Hajjah Fatimah',
      },
      {
        status: 'PENDING_APPROVAL' as ApplicationStatus,
        changedAt: new Date('2024-11-25T09:00:00'),
        changedBy: 'user-007',
        changedByName: 'Sarah Lee',
      },
      {
        status: 'APPROVED' as ApplicationStatus,
        changedAt: new Date('2024-12-01T10:00:00'),
        changedBy: 'user-007',
        changedByName: 'Sarah Lee',
        comment: 'Approved for halal certification',
      },
    ],

    submittedAt: new Date('2024-09-10T16:00:00'),
    targetCompletionDate: new Date('2024-12-10T23:59:59'),
    completedAt: new Date('2024-12-01T10:00:00'),

    createdAt: new Date('2024-09-01T08:00:00'),
    updatedAt: new Date('2024-12-01T10:00:00'),
    createdBy: 'user-001',
    updatedBy: 'user-007',
  },
];

/**
 * Helper functions
 */

export function getApplicationById(id: string): Application | undefined {
  return mockApplications.find((app) => app.id === id);
}

export function getApplicationsByStatus(status: ApplicationStatus): Application[] {
  return mockApplications.filter((app) => app.status === status);
}

export function getApplicationsByCompany(companyId: string): Application[] {
  return mockApplications.filter((app) => app.companyId === companyId);
}

export function getApplicationsByConsultant(consultantId: string): Application[] {
  return mockApplications.filter((app) => app.assignedConsultantId === consultantId);
}

export function getApplicationsByAuditor(auditorId: string): Application[] {
  return mockApplications.filter((app) => app.assignedAuditorId === auditorId);
}

export function getDraftApplications(): Application[] {
  return getApplicationsByStatus('DRAFT' as ApplicationStatus);
}

export function getActiveApplications(): Application[] {
  const activeStatuses: ApplicationStatus[] = [
    'SUBMITTED',
    'UNDER_REVIEW',
    'CONSULTANT_ASSIGNED',
    'PRE_AUDIT_SCHEDULED',
    'PRE_AUDIT_COMPLETED',
    'AUDIT_SCHEDULED',
    'AUDIT_IN_PROGRESS',
    'AUDIT_COMPLETED',
    'PENDING_APPROVAL',
  ] as ApplicationStatus[];

  return mockApplications.filter((app) => activeStatuses.includes(app.status));
}

export function getCompletedApplications(): Application[] {
  const completedStatuses: ApplicationStatus[] = ['APPROVED', 'REJECTED', 'WITHDRAWN', 'CANCELLED'] as ApplicationStatus[];
  return mockApplications.filter((app) => completedStatuses.includes(app.status));
}

export function getApplicationStatistics() {
  return {
    total: mockApplications.length,
    draft: getDraftApplications().length,
    active: getActiveApplications().length,
    completed: getCompletedApplications().length,
    approved: getApplicationsByStatus('APPROVED' as ApplicationStatus).length,
    rejected: getApplicationsByStatus('REJECTED' as ApplicationStatus).length,
  };
}
